import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COUNTRIES, PRIORITY_ISO, byIso } from '../utils/countryCodes';

/**
 * Phone field with a country dropdown welded to its left edge — flag + dial code
 * as the button, a searchable list on open, the national number to its right.
 *
 * Reports `{ country, number }` and never a joined string: the server resolves
 * the dial code from the ISO country itself, so the two halves have to stay
 * separate all the way down. Canada and the US both being +1 is exactly why the
 * ISO code is the identity here and the dial code is only ever a label.
 */
export default function PhoneInput({
  country = 'CA',
  number = '',
  onChange,
  disabled = false,
  error = null,
  autoFocus = false,
  id = 'phone',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const selected = byIso(country) || byIso('CA');

  // Close on an outside click or Escape, like any other menu on the site.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (c) => !q
      || c.name.toLowerCase().includes(q)
      || c.iso.toLowerCase() === q
      || c.dial.replace('+', '').startsWith(q.replace('+', ''));
    if (q) return COUNTRIES.filter(match);
    // No search yet: put +1 at the top, since that's every current user.
    const pinned = PRIORITY_ISO.map(byIso).filter(Boolean);
    return [...pinned, ...COUNTRIES.filter((c) => !PRIORITY_ISO.includes(c.iso))];
  }, [query]);

  const pick = (iso) => {
    setOpen(false);
    setQuery('');
    onChange({ country: iso, number });
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className={`flex border-2 ${error ? 'border-red-600' : 'border-black'} bg-white`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-label={`Country: ${selected?.name}`}
          aria-expanded={open}
          className="flex flex-shrink-0 items-center gap-2 border-r-2 border-black px-3 py-3 font-mono text-sm text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="text-base leading-none" aria-hidden>{selected?.flag}</span>
          <span className="tracking-wider">{selected?.dial}</span>
          <span aria-hidden className="text-[10px] leading-none">▾</span>
        </button>

        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus={autoFocus}
          value={number}
          onChange={(e) => onChange({ country, number: e.target.value })}
          placeholder="250 555 0143"
          disabled={disabled}
          maxLength={24}
          className="min-w-0 flex-1 bg-white px-4 py-3 font-mono text-sm text-black placeholder-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {error && <p className="mt-2 font-mono text-xs text-red-600">{error}</p>}

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 border-2 border-black bg-white shadow-hard">
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search countries..."
            className="w-full border-b-2 border-black px-3 py-2.5 font-mono text-sm text-black placeholder-gray-400 focus:outline-none"
          />
          <div className="max-h-64 overflow-y-auto">
            {list.length === 0 ? (
              <div className="px-3 py-4 text-center font-mono text-xs text-gray-500">No match.</div>
            ) : list.map((c, i) => {
              // A separator under the pinned +1 pair, so they read as shortcuts
              // rather than as the alphabetical start of the list.
              const endsPinned = !query && i === PRIORITY_ISO.length - 1;
              return (
                <button
                  key={c.iso}
                  type="button"
                  onClick={() => pick(c.iso)}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left font-mono text-sm transition-colors ${
                    endsPinned ? 'border-b-2 border-black' : ''
                  } ${c.iso === country ? 'bg-black text-white' : 'text-black hover:bg-gray-100'}`}
                >
                  <span className="text-base leading-none" aria-hidden>{c.flag}</span>
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  <span className="flex-shrink-0 opacity-70">{c.dial}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
